import { post, get } from '../utils/api.utils';
import { API_ENDPOINTS } from '../constants/endpoints';

// Generic type for events logger response
export interface EventsLoggerResponse<T = any> {
    data: {
        success: boolean;
        message: string;
        event?: T;
        events?: T[];
    };
    timestamp: string;
    status: number;
}


// Type for event object
export interface EventObject<T = any> {
    id: string;
    email: string;
    event_type: string;
    event_data: T;
    created_at: string;
}

export default class EventsLoggerService {
    public static async logEvent(email: string, eventType: string, eventData: any) {
        try {
            const response = await post(
                API_ENDPOINTS.EVENTS_LOGGER.LOG_EVENT,
                {
                    email: email,
                    event_type: eventType,
                    event_data: eventData
                }
            );

            return response;
        } catch (error: any) {
            if (error.response?.status === 400) {
                throw new Error('Invalid event data provided');
            }
            if (error.response?.status === 401) {
                throw new Error('Unauthorized. Please login again');
            }
            if (error.response?.status === 429) {
                throw new Error('Too many event logging attempts. Please try again later');
            }
            if (error.response?.status === 500) {
                throw new Error('Internal server error. Please try again later');
            }
            
            throw new Error('An error occurred while logging event. Please try again');
        }
    }

    public static async getEventsByEmail(email: string) {
        try {
            const response = await get(
                `${API_ENDPOINTS.EVENTS_LOGGER.EVENTS_BY_EMAIL}/${email}`
            );

            return response;
        } catch (error: any) {
            if (error.response?.status === 400) {
                throw new Error('Invalid email format provided');
            }
            if (error.response?.status === 401) {
                throw new Error('Unauthorized. Please login again');
            }
            if (error.response?.status === 404) {
                throw new Error('No events found for this email');
            }
            if (error.response?.status === 429) {
                throw new Error('Too many requests. Please try again later');
            }
            if (error.response?.status === 500) {
                throw new Error('Internal server error. Please try again later');
            }
            
            throw new Error('An error occurred while fetching events. Please try again');
        }
    }

    public static async getLatestSignupEvent(email: string): Promise<EventsLoggerResponse<EventObject>> {
        try {
            const response = await get<EventsLoggerResponse<EventObject>>(
                `${API_ENDPOINTS.EVENTS_LOGGER.LATEST_SIGNUP_EVENT}/${email}`
            );

            return response;
        } catch (error: any) {
            if (error.response?.status === 400) {
                throw new Error('Invalid email format provided');
            }
            if (error.response?.status === 401) {
                throw new Error('Unauthorized. Please login again');
            }
            if (error.response?.status === 404) {
                throw new Error('No signup event found for this email');
            }
            if (error.response?.status === 429) {
                throw new Error('Too many requests. Please try again later');
            }
            if (error.response?.status === 500) {
                throw new Error('Internal server error. Please try again later');
            }
            
            throw new Error('An error occurred while fetching latest signup event. Please try again');
        }
    }

    public static async getEventsByType(eventType: string): Promise<EventsLoggerResponse<EventObject[]>> {
        try {
            const response = await get<EventsLoggerResponse<EventObject[]>>(
                `${API_ENDPOINTS.EVENTS_LOGGER.EVENTS_BY_TYPE}/${eventType}`
            );

            return response;
        } catch (error: any) {
            if (error.response?.status === 400) {
                throw new Error('Invalid event type provided');
            }
            if (error.response?.status === 401) {
                throw new Error('Unauthorized. Please login again');
            }
            if (error.response?.status === 404) {
                throw new Error('No events found for this event type');
            }
            if (error.response?.status === 429) {
                throw new Error('Too many requests. Please try again later');
            }
            if (error.response?.status === 500) {
                throw new Error('Internal server error. Please try again later');
            }
            
            throw new Error('An error occurred while fetching events by type. Please try again');
        }
    }
}